import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { observer } from "mobx-react";
import React, { useCallback } from "react";
import styled from "styled-components";

import {
  Carousel,
  CarouselHorizontalScrollCarouselItem,
  IProps as ICarouselProps,
} from "@faire/retailer/components/Carousel/Carousel";
import { ILeanProductTile } from "@faire/retailer/features/MarketplaceItems/MarketplaceProduct/LeanProtos/ILeanProductTile";
import { ProductImage } from "@faire/retailer/features/MarketplaceItems/ProductImage";
import { ProductTile } from "@faire/retailer/features/MarketplaceItems/ProductTile";
import {
  popSignUp,
  SignUpElement,
  SignUpType,
} from "@faire/retailer/lib/popSignUp";

const ProductPreviewCarouselBase: React.FC<IProps> = (props) => {
  const { productTiles, brandToken, className, moreProductCount } = props;
  const viewport = useViewport();

  const itemsPerSlide = useResponsiveValue({
    mobileAndAbove: 2,
    tabletAndAbove: 3,
    desktopAndAbove: 3,
    xLargeAndAbove: 4,
  });

  const handleSignUp = useCallback(
    () =>
      popSignUp(SignUpType.FAIRE_DIRECT, () => null, {
        element: SignUpElement.CAROUSEL,
        sourceId: `fd_landing_product_preview_carousel`,
      }),
    []
  );

  const renderItem = (productTile: ILeanProductTile) => {
    const isLastItem =
      props.productTiles.length > 0 &&
      productTile === props.productTiles.slice(-1)[0];

    const bestProductImage = productTile.best_image;

    if (isLastItem && bestProductImage && moreProductCount) {
      return (
        <SpecialTile onClick={handleSignUp}>
          <ProductImage
            image={bestProductImage}
            imageDimension={{
              mobileAndAbove: 80,
              tabletAndAbove: 250,
            }}
            alt={
              productTile.product?.description ||
              productTile?.product?.name ||
              ""
            }
            extraProductsCount={moreProductCount}
          />
        </SpecialTile>
      );
    } else {
      return (
        <ProductTile
          key={productTile?.product?.token}
          productTile={productTile}
          generateProductLinkProps={() => undefined}
          imageDimension={{
            mobileAndAbove: 80,
            tabletAndAbove: 250,
          }}
        />
      );
    }
  };

  return (
    <StyledCarousel
      className={className}
      padItems
      name={`fd-landing/${brandToken}/product-list`}
      items={productTiles}
      noWrap
      peekNext={viewport.isMobile}
      itemGap={8}
      itemsPerSlide={itemsPerSlide}
      renderItem={renderItem}
      buttonsOffsetTop="calc(50% - 50px)"
    />
  );
};

export const ProductPreviewCarousel = observer(ProductPreviewCarouselBase);

interface IProps {
  className?: string;
  brandToken: string;
  productTiles: ILeanProductTile[];
  moreProductCount: number;
}

const SpecialTile = styled.div`
  cursor: pointer;
  height: fit-content;
`;

const StyledCarousel = styled(
  Carousel as React.ComponentType<ICarouselProps<ILeanProductTile>>
)`
  width: calc(100% + 40px);
  margin: 0 -20px;
  ${"" /* This hides the horizontal scrollbar on mobile */};
  overflow-y: hidden;

  ${responsiveMediaQueries.tabletAndAbove} {
    overflow-y: unset;
    width: 100%;
    margin: 0;
  }

  ${CarouselHorizontalScrollCarouselItem} {
    padding: 0 4px;

    &:last-child {
      padding: 0 20px 0 4px;
    }
  }
`;
